import { Users } from "lucide-react-native";
import { FC, useEffect, useRef, useState } from "react";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";

import { Box, HStack, Skeleton, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";
import { CountryValue } from "@/types/resources";

interface PersonasLoaderProps {
  countries: CountryValue[];
}

const TOTAL = 12;
const COLS = 4;
const ROTATION_MS = 1800;

const GENDER_KEYS: ("female" | "male" | "non_binary")[] = [
  "female",
  "female",
  "female",
  "female",
  "female",
  "female",
  "female",
  "female",
  "female",
  "male",
  "male",
  "male",
  "male",
  "male",
  "male",
  "male",
  "male",
  "male",
  "non_binary",
];

const toFlag = (code: string) =>
  [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");

const chunk = <T,>(arr: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size)
  );

const randomAge = () => Math.floor(Math.random() * (65 - 20 + 1)) + 20;

const randomFlag = (
  pool: CountryValue[],
  currentFlag?: string | null
): string | null => {
  if (pool.length === 0) return null;
  if (pool.length === 1) return toFlag(pool[0]);
  const others = pool.filter((c) => toFlag(c) !== currentFlag);
  return toFlag(others[Math.floor(Math.random() * others.length)]);
};

type CardState = { demographic: string; flag: string | null };

type PersonaCardProps = {
  delay: number;
  demographic: string;
  flag: string | null;
};

const PersonasSkeleton: FC = () => (
  <VStack className="gap-2">
    {Array.from({ length: 3 }).map((_, rowIdx) => (
      <HStack key={rowIdx} className="gap-2">
        {Array.from({ length: 4 }).map((__, colIdx) => (
          <Box
            key={colIdx}
            className="flex-1 items-center rounded-xl border border-outline-100 bg-background-0 p-4 gap-2"
          >
            <Skeleton className="w-8 h-5 rounded" />
            <Skeleton className="w-10 h-10 rounded-full" />
            <Skeleton className="h-2.5 w-full rounded" />
          </Box>
        ))}
      </HStack>
    ))}
  </VStack>
);

const PersonaCard: FC<PersonaCardProps> = ({ delay, demographic, flag }) => {
  const opacity = useSharedValue(0);
  const scale = useSharedValue(0.8);
  const mounted = useRef(false);
  const prevContent = useRef(`${flag}|${demographic}`);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      opacity.value = withDelay(delay, withTiming(1, { duration: 400 }));
      scale.value = withDelay(
        delay,
        withTiming(1, { duration: 400, easing: Easing.out(Easing.cubic) })
      );
      return;
    }
    const content = `${flag}|${demographic}`;
    if (content === prevContent.current) return;
    prevContent.current = content;
    opacity.value = withTiming(0.3, { duration: 150 }, () => {
      "worklet";
      opacity.value = withTiming(1, { duration: 350 });
    });
  }, [flag, demographic, delay, opacity, scale]);

  const style = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  return (
    <Animated.View style={[style, { flex: 1 }]}>
      <Box className="flex-1 items-center rounded-xl border border-outline-100 bg-background-0 p-4 gap-2">
        <Text className="text-2xl">{flag}</Text>
        <Box className="w-10 h-10 rounded-full bg-background-100 items-center justify-center">
          <Users
            color="rgb(var(--color-typography-400))"
            size={18}
            strokeWidth={1.5}
          />
        </Box>
        <Text
          className="text-xs font-medium text-typography-700 text-center"
          numberOfLines={1}
        >
          {demographic}
        </Text>
      </Box>
    </Animated.View>
  );
};

const PersonasLoader: FC<PersonasLoaderProps> = ({ countries }) => {
  const { i18n } = useLocalization();
  const poolRef = useRef<CountryValue[]>([]);
  const initializedRef = useRef(false);

  const randomDemographic = () => {
    const key = GENDER_KEYS[Math.floor(Math.random() * GENDER_KEYS.length)];
    const genderLabel = {
      female: i18n.t("demographics.female"),
      male: i18n.t("demographics.male"),
      non_binary: i18n.t("demographics.nonBinary"),
    }[key];
    return `${genderLabel}, ${randomAge()}`;
  };

  const [cards, setCards] = useState<CardState[]>(() =>
    Array.from({ length: TOTAL }, () => ({
      demographic: randomDemographic(),
      flag: randomFlag([]),
    }))
  );

  useEffect(() => {
    poolRef.current = countries;
    if (countries.length === 0 || initializedRef.current) return;
    initializedRef.current = true;
    setCards(
      Array.from({ length: TOTAL }, () => ({
        demographic: randomDemographic(),
        flag: randomFlag(countries, null),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCards((prev) => {
        const next = [...prev];
        const indices = [...Array(TOTAL).keys()]
          .sort(() => Math.random() - 0.5)
          .slice(0, 8);
        indices.forEach((i) => {
          next[i] = {
            demographic: randomDemographic(),
            flag: randomFlag(poolRef.current, prev[i].flag),
          };
        });
        return next;
      });
    }, ROTATION_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (countries.length === 0) return <PersonasSkeleton />;

  const rows = chunk(cards, COLS);

  return (
    <VStack className="gap-2">
      {rows.map((row, rowIdx) => (
        <HStack key={rowIdx} className="gap-2">
          {row.map((p, i) => (
            <PersonaCard
              key={rowIdx * COLS + i}
              delay={(rowIdx * COLS + i) * 80}
              demographic={p.demographic}
              flag={p.flag}
            />
          ))}
        </HStack>
      ))}
    </VStack>
  );
};

export default PersonasLoader;
